import { createJSONStorage, type StateStorage } from 'zustand/middleware';

export const AUTH_STORAGE_KEY = 'auth-storage';
export const TOKEN_KEY = 'token';

const PERSISTENCE_MODE_KEY = 'auth-persistence-mode';
const KEEP_LOGGED_IN_PREF_KEY = 'auth-keep-logged-in';

export type AuthPersistenceMode = 'local' | 'session';

function getStorageForMode(mode: AuthPersistenceMode): Storage {
  return mode === 'session' ? sessionStorage : localStorage;
}

export function getAuthPersistenceMode(): AuthPersistenceMode {
  return localStorage.getItem(PERSISTENCE_MODE_KEY) === 'session' ? 'session' : 'local';
}

export function setAuthPersistenceMode(mode: AuthPersistenceMode): void {
  localStorage.setItem(PERSISTENCE_MODE_KEY, mode);
}

export function getActiveAuthStorage(): Storage {
  return getStorageForMode(getAuthPersistenceMode());
}

export function getKeepLoggedInPreference(): boolean {
  const saved = localStorage.getItem(KEEP_LOGGED_IN_PREF_KEY);
  if (saved === null) return true;
  return saved === 'true';
}

export function setKeepLoggedInPreference(keepLoggedIn: boolean): void {
  localStorage.setItem(KEEP_LOGGED_IN_PREF_KEY, String(keepLoggedIn));
}

export function clearAllAuthStorage(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  sessionStorage.removeItem(AUTH_STORAGE_KEY);
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
}

export function getAuthToken(): string | null {
  const mode = getAuthPersistenceMode();
  const primary = getStorageForMode(mode);
  const fallback = getStorageForMode(mode === 'session' ? 'local' : 'session');
  return primary.getItem(TOKEN_KEY) || fallback.getItem(TOKEN_KEY);
}

export function setAuthToken(token: string, keepLoggedIn: boolean): void {
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  getStorageForMode(keepLoggedIn ? 'local' : 'session').setItem(TOKEN_KEY, token);
}

const authPersistStorage: StateStorage = {
  getItem: (name): string | null => {
    const mode = getAuthPersistenceMode();
    const primary = getStorageForMode(mode);
    const value = primary.getItem(name);
    if (value !== null) return value;

    const fallback = getStorageForMode(mode === 'session' ? 'local' : 'session');
    return fallback.getItem(name);
  },
  setItem: (name, value): void => {
    getActiveAuthStorage().setItem(name, value);
  },
  removeItem: (name): void => {
    localStorage.removeItem(name);
    sessionStorage.removeItem(name);
  },
};

export const authPersistStorageAdapter = createJSONStorage(() => authPersistStorage);
